import { forwardRef, useRef, useEffect } from 'react';
import { cx } from '@trinity/chat-components/utils';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import type { VideoPlayerProps } from './interfaces';
import styles from './style/VideoPlayer.module.css';

type Props =
  VideoPlayerProps;

const VideoPlayer = ({
  children = (<p>Your browser does not support the <code>video</code> tag.</p>),
  onEnded,
  disableSeeking,
  ...props
}: Props, ref: React.ForwardedRef<HTMLVideoElement>) => {
  const internalRef = useRef<HTMLVideoElement>(undefined);
  useKeyboardShortcuts(internalRef);
  useEffect(() => {
    const videoElement = internalRef.current;
    if (videoElement && onEnded) {
      const handleEnded = () => {
        onEnded();
      };

      videoElement.addEventListener('ended', handleEnded);

      return () => {
        videoElement.removeEventListener('ended', handleEnded);
      };
    }
  }, [onEnded]);

  return (
    <video
      ref={n => {
        internalRef.current = n;

        if (!ref) return;

        if (typeof ref === 'function') {
          ref(n);
        } else {
          ref.current = n;
        }
      }}
      autoPlay={props.autoplay}
      className={cx(styles.videoPlayer, props.className, {
        [styles.disableSeeking]: disableSeeking,
      })}
      crossOrigin="anonymous"
      controls={props.controls}
      style={props.style}
      preload={props.preload ?? 'metadata'}
      poster={props.poster}
      playsInline
      src={props.src}>
      {children}
    </video>
  );
};

const VideoPlayerWithRef = forwardRef<HTMLVideoElement, Props>(VideoPlayer);

export { VideoPlayerWithRef as VideoPlayer };
export default VideoPlayerWithRef;